/**
 * Client half of the "an AI rewrite never costs a description its links" guard
 * (server half: functions/src/descriptionLinkGuard.ts).
 *
 * Brand, manufacturer and parent-org descriptions are HTML carrying in-app
 * entity links (`<a href="watchkeeper://open/{kind}/{id}" data-wk-entity
 * data-wk-id>`). An accepted AI description replaces that HTML wholesale, and
 * the review table shows both sides as plain text — so a rewrite that dropped
 * every link looked identical to one that kept them.
 *
 * The callables withhold such a proposal and report it as `descriptionLinkLoss`.
 * This module re-checks on the client too, because a live function can lag
 * its merged code by weeks: the editor must not offer a link-losing description
 * whatever the deployed function returns.
 */

import { ENTITY_LINK_PREFIX } from './entityLinks';

/** One withheld description proposal (same shape the callables return). */
export interface DescriptionLinkLoss {
  field: string;
  inputLinks: number;
  outputLinks: number;
  missing: string[];
}

/** Distinct `kind:id` keys of the entity links in an HTML fragment, in order. */
export function entityLinkKeys(html: string | null | undefined): string[] {
  if (!html || !html.includes('<a')) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const keys: string[] = [];
  for (const a of Array.from(doc.querySelectorAll('a'))) {
    let kind = (a.getAttribute('data-wk-entity') ?? '').trim();
    let id = (a.getAttribute('data-wk-id') ?? '').trim();
    const href = (a.getAttribute('href') ?? '').trim();
    if ((!kind || !id) && href.startsWith(ENTITY_LINK_PREFIX)) {
      const rest = href.slice(ENTITY_LINK_PREFIX.length);
      const slash = rest.indexOf('/');
      if (slash > 0) {
        kind = kind || rest.slice(0, slash);
        id = id || decodeURIComponent(rest.slice(slash + 1));
      }
    }
    if (!kind || !id) continue;
    const key = `${kind}:${id}`;
    if (!keys.includes(key)) keys.push(key);
  }
  return keys;
}

/** How many distinct entity links a description carries. */
export const countEntityLinks = (html: string | null | undefined): number => entityLinkKeys(html).length;

/**
 * Drop every description proposal that loses a link of `currentHtml`, and
 * gather the losses (the callable's report plus anything caught here).
 *
 * @param result     the callable's answer; not mutated
 * @param currentHtml the description as the editor holds it now
 * @param fields     `html` keys are compared link by link; `plain` keys hold
 *                   plain text, so any proposal for one loses every link
 */
export function withholdLinkLosingDescriptions<
  R extends {
    suggestions: Record<string, unknown>;
    confidence: Record<string, 'high' | 'medium' | 'low'>;
    descriptionLinkLoss?: DescriptionLinkLoss[];
  },
>(
  result: R,
  currentHtml: string | null | undefined,
  fields: { html: readonly string[]; plain?: readonly string[] },
): { result: R; losses: DescriptionLinkLoss[] } {
  const losses: DescriptionLinkLoss[] = [...(result.descriptionLinkLoss ?? [])];
  const inputKeys = entityLinkKeys(currentHtml);
  const suggestions = { ...(result.suggestions ?? {}) };
  const confidence = { ...(result.confidence ?? {}) };
  if (inputKeys.length > 0) {
    const check = (field: string, outputKeys: string[]) => {
      const missing = inputKeys.filter((k) => !outputKeys.includes(k));
      if (missing.length === 0) return;
      if (!losses.some((l) => l.field === field)) {
        losses.push({ field, inputLinks: inputKeys.length, outputLinks: outputKeys.length, missing });
      }
      delete suggestions[field];
      delete confidence[field];
    };
    for (const field of fields.html) {
      const v = suggestions[field];
      if (typeof v === 'string') check(field, entityLinkKeys(v));
    }
    for (const field of fields.plain ?? []) {
      if (field in suggestions) check(field, []);
    }
  }
  return { result: { ...result, suggestions, confidence }, losses };
}

/** The curator-facing explanation of a withheld description, or null. */
export function describeLinkLoss(losses: readonly DescriptionLinkLoss[]): string | null {
  if (losses.length === 0) return null;
  const worst = losses.reduce((a, b) => (b.missing.length > a.missing.length ? b : a));
  return (
    `AI description not offered: it dropped ${worst.missing.length} of the ${worst.inputLinks} ` +
    `in-app link${worst.inputLinks === 1 ? '' : 's'} in the current description ` +
    `(${worst.missing.join(', ')}). The stored description is unchanged — edit it by hand if it needs updating.`
  );
}
